import { isReservedVariable } from '@/utils/promptUtils';
import { Icon } from '@iconify/react';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  cn,
} from '@nextui-org/react';
import { useEffect, useRef, useState } from 'react';

interface VariableDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  variables: Record<string, string>;
  selectedVariable: string;
  onSubmit: (variables: Record<string, string>) => void;
}

export default function VariableDrawer({
  isOpen,
  onClose,
  variables,
  onSubmit,
  selectedVariable,
}: VariableDrawerProps) {
  const [variableValues, setVariableValues] =
    useState<Record<string, string>>(variables);
  const [searchQuery, setSearchQuery] = useState('');
  const selectedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedVariable && selectedRef.current) {
      selectedRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [selectedVariable, isOpen]);

  const handleSubmit = () => {
    onSubmit(variableValues);
    setVariableValues({});
  };

  const handleVariableChange = (variableName: string, value: string) => {
    setVariableValues((prev) => ({
      ...prev,
      [variableName]: value,
    }));
    // if (setVariablesState) {
    //   setVariablesState({ [variableName]: value });
    // }
  };

  const filteredVariables = Object.keys(variables || {}).filter(
    (variableName) =>
      variableName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      hideCloseButton
      classNames={{
        wrapper: '!items-start !justify-end',
        base: '!m-0 rounded-medium h-[calc(100vh-64px)] w-full sm:w-[600px]',
        backdrop: 'hidden',
      }}
      motionProps={{
        variants: {
          enter: {
            x: 0,
            transition: { duration: 0.3 },
          },
          exit: {
            x: 400,
            transition: {
              duration: 0.2,
              ease: 'easeIn',
            },
          },
        },
      }}
    >
      <ModalContent className="absolute top-2 right-2 h-[calc(100vh-20px)]">
        {(onClose) => (
          <>
            <ModalHeader className="flex justify-between items-center border-b">
              <span>Variables</span>
              <Button isIconOnly variant="light" onPress={onClose}>
                <Icon icon="mdi:close" className="h-5 w-5" />
              </Button>
            </ModalHeader>

            <ModalBody className="overflow-y-auto">
              <div className="sticky -top-2 pt-2 pb-2 bg-background z-10">
                <input
                  type="text"
                  placeholder="Search variables..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-3 py-2 rounded-md border border-default-200 bg-default-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div className="space-y-4">
                {filteredVariables.map((variableName) => (
                  <div
                    key={variableName}
                    className={cn(
                      'space-y-2 p-2 rounded-lg transition-colors',
                      selectedVariable === variableName && 'bg-primary/5'
                    )}
                    ref={
                      selectedVariable === variableName
                        ? selectedRef
                        : undefined
                    }
                  >
                    <label
                      className={cn(
                        'text-sm font-medium',
                        selectedVariable === variableName && 'text-primary'
                      )}
                    >
                      {variableName}
                    </label>
                    <textarea
                      disabled={isReservedVariable(variableName)}
                      value={variableValues[variableName] || ''}
                      onChange={(e) =>
                        handleVariableChange(variableName, e.target.value)
                      }
                      placeholder={`Enter value for ${variableName}`}
                      className={cn(
                        'w-full min-h-[80px] rounded-md border bg-default-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary',
                        selectedVariable === variableName &&
                          !isReservedVariable(variableName)
                          ? 'border-primary'
                          : 'border-default-200'
                      )}
                    />
                  </div>
                ))}
              </div>

              <ModalFooter className="flex justify-between border-t pt-4 px-0">
                <Button variant="bordered" onPress={onClose}>
                  Cancel
                </Button>
                <Button
                  color="primary"
                  onPress={handleSubmit}
                  //   startContent={<Icon icon="mdi:check" className="h-5 w-5" />}
                >
                  Save
                </Button>
              </ModalFooter>
            </ModalBody>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
